import Debug from 'debug';
import { ok, err, Result } from '@xlearn/common'
import { shuffleArray } from '@/app/shared/utils';
import { LoadQuestionsJob, LoadQuestionsError, Question } from '@/app/question/store';
import { QuestionReference } from '@/app/quiz'
import { CancelToken } from '@/app/shared/command-queue'
import { LoadExamJob, LoadExamError } from './load-exam-job'
import { ExamStateMachineAbstractState, ExamStartOptions, ExamStateMachineCommonEvents } from './state'
import { ExamQuestion, ExamStateMachineData } from '../data'
import { ExamSessionService } from '@/app/exam-session/service';

const debug = Debug('exam-starting-state')

interface Events extends ExamStateMachineCommonEvents {
  onStarted: () => void
}

type CommonErrorCode = LoadExamError | LoadQuestionsError
type StartErrorCode = CommonErrorCode
type ContinueErrorCode = CommonErrorCode | 'continue-question-not-found'

export class ExamStartingState extends ExamStateMachineAbstractState<Events> {

  public readonly name = 'starting'

  constructor(
    private startOptions: ExamStartOptions,
    private loadExamJob: LoadExamJob,
    private loadQuestionsJob: LoadQuestionsJob,
    private data: ExamStateMachineData,
    private examSessionService: ExamSessionService) {

    super()
    debug('constructor', startOptions)
  }
  
  enter() {
    debug('enter')
    const startJob = this.execute(this.cancelToken)
    startJob.catch(err => {
      debug('startJob error', err)
      this.triggerEvent(events => events.onError(err))
    })
  }

  exit() {
    debug('exit')
  }

  cancel() {
    debug('beginCancel')
    this.beginCancel()
  }

  private async execute(cancelToken: CancelToken) {
    let result: Result<true, StartErrorCode | ContinueErrorCode>
    switch (this.startOptions.type) {
      case 'new':
        result = await this.startNew(cancelToken)        
        break
      case 'continue':
        result = await this.continueExisting(cancelToken)
        break
    }

    const isCanceled = cancelToken.cancel ||
          (!result.success && result.error === 'canceled')
    if (isCanceled) {
      debug('endCancel')
      this.endCancel()
      return
    }

    if (!result.success) {
      const error = result.error
      this.triggerEvent(events => events.onError(error))
      return
    }

    this.triggerEvent(events => events.onStarted())
  }

  private async startNew(cancelToken: CancelToken): Promise<Result<true, StartErrorCode>> {
    const { sessionId, examId, aspect } = this.startOptions
    debug('starting new exam session', sessionId, examId)
    
    const examResult = await this.loadExam(examId, cancelToken)
    if (!examResult.success) {
      return examResult
    }

    const exam = examResult.value

    const questionReferences = this.shuffleQuestions(exam.questions, exam.questionCount)
    const questionsResult = await this.loadQuestions(questionReferences, cancelToken)
    if (!questionsResult.success) {
      return questionsResult
    }

    const questions = questionsResult.value
    
    const startTime = new Date()
    this.examSessionService.startSession(sessionId, examId, aspect, {
      startTime,
      questionIds: questions.map(x => x.id),
      questionIndex: 0
    })

    const examDetails = this.examDetails(exam)
    this.data.started({ sessionId, startTime, questions, examDetails })

    debug('exam starting finished', questions)

    return ok(true)
  }

  private examDetails(exam: { id: string, percentToPass: number, timeLimitSeconds: number}) {
    return {
      id: exam.id,
      percentToPass: exam.percentToPass,
      timeLimitSeconds: exam.timeLimitSeconds
    };
  }

  private async continueExisting(cancelToken: CancelToken): Promise<Result<true, ContinueErrorCode>> {
    if (!('state' in this.startOptions)) {
      throw new Error('Coding error: missing state')
    }
    
    const { sessionId, examId, state } = this.startOptions
     debug('continuing exam session', sessionId, examId)

    const examResult = await this.loadExam(examId, cancelToken)
    if (!examResult.success) {
      return examResult
    }
    const exam = examResult.value

    const questionIds = state.questionIds
    const questionReferencesResult = this.filterReferencesByQuestionIds(exam.questions, questionIds)
    if (!questionReferencesResult.success) {
      return questionReferencesResult
    }
    const questionReferences = questionReferencesResult.value

    const questionsResult = await this.loadQuestions(questionReferences, cancelToken)
    if (!questionsResult.success) { return questionsResult }
    const questions = questionsResult.value

    const examDetails = this.examDetails(exam)
    this.data.continued({ sessionId,
                          startTime: state.startTime,
                          questions,
                          examDetails }, {
                            questionStates: state.questionStates,
                            navigation: state.navigation,
                            questionTimes: state.questionTimes
                          })

    debug('exam continuation finished', questions)
    return ok(true)
  }

  private filterReferencesByQuestionIds(questionReferences: QuestionReference[], questionIds: string[]) {
    const filtered = questionIds.map(id => {
      return questionReferences.find(x => x.questionId == id)
    })

    for (let item of filtered) {
      if (!item) {
        return err('continue-question-not-found' as 'continue-question-not-found')
      }
    }

    return ok(filtered as QuestionReference[])
  }

  private async loadQuestions(references: QuestionReference[], cancelToken: CancelToken) {
    const questionResult = await this.loadQuestionsJob.execute(
      references.map(q => q.questionId), cancelToken)
    if (!questionResult.success) {
      return questionResult
    }

    const questions = questionResult.value

    const examQuestions = references.map((ref, i) => {
      return this.mapExamQuestion(ref, questions[i])
    })

    return ok(examQuestions)
  }

  private async loadExam(examId: string, cancelToken: CancelToken) {
    const examResult = await this.loadExamJob.execute(examId, cancelToken)
    return examResult
  }

  private mapExamQuestion(ref: QuestionReference, question: Question): ExamQuestion {
    const examQuestion: ExamQuestion = {
      id: ref.questionId,
      type: question.type,
      minScore: ref.minScore,
      maxScore: ref.maxScore,
      question: question.question,
      feedback: question.feedback,
      content: question.content,
      contentScore: question.contentScore
    }
    return examQuestion    
  }

  private shuffleQuestions(questions: QuestionReference[], count: number) {
    return shuffleArray(questions, count)
  }
}
